import { Component, Host, h, Event, EventEmitter, State, Method, Prop } from '@stencil/core';
import { i18nUpdateComponent } from '../sy-site-i18n/sy-site-i18n-hooks';

@Component({
  tag: 'sy-site-language-header',
  styleUrl: 'language-header.scss',
  shadow: true,
})
export class LanguageHeader {
  @i18nUpdateComponent()
  @Prop() siteI18n!: HTMLSySiteI18nElement;
  @Prop() siteApi!: HTMLSySiteApiElement;
  private observer: MutationObserver;

  @State() visible = false;

  @State() href: string;

  @State() chooseLanguage: string;

  @State() userLang: string;

  /**
   * Emmitted when the language header is open
   */
  @Event() sySiteLanguageHeaderDidOpen: EventEmitter;

  /**
   * Emmitted when the language header is closed
   */
  @Event() sySiteLanguageHeaderDidClose: EventEmitter;

  private close() {
    this.visible = false;
    this.sySiteLanguageHeaderDidClose.emit();
  }

  private open() {
    this.visible = true;
    this.sySiteLanguageHeaderDidOpen.emit();
  }

  private getLanguagePreference() {
    return JSON.parse(localStorage.getItem('languagePreference'));
  }

  private setLanguagePreference(language) {
    // O idioma preferencial salvo no local storage do usuário deverá ser considerado tendo validade de 7 dias.
    const startDate = new Date().getTime();
    localStorage.setItem('languagePreference', JSON.stringify({ preferredLanguage: language.slice(0, 2), startDate: startDate }));
  }

  private isLanguagePreferenceExpired() {
    const daysToExpire = 7;
    const storedPreference = this.getLanguagePreference();
    if (storedPreference) {
      // One day in milliseconds
      const oneDay = 1000 * 60 * 60 * 24;
      // Calculating the time difference between two dates
      const diffInTime = new Date().getTime() - storedPreference.startDate;
      // Calculating the no. of days between two dates
      const diffInDays = Math.floor(diffInTime / oneDay);
      return diffInDays > daysToExpire;
    } else {
      return true;
    }
  }

  private async getLang() {
    if (this.isLanguagePreferenceExpired()) {
      if (sessionStorage.getItem('userCountryLanguage')) {
        return sessionStorage.getItem('userCountryLanguage');
      } else {
        return this.siteApi.getCountryLanguage().then(userLang => {
          sessionStorage.setItem('userCountryLanguage', userLang.lang);
          return userLang.lang;
        });
      }
    } else {
      return this.getLanguagePreference().preferredLanguage;
    }
  }

  /**
   * Sets up the language preferences
   */
  @Method()
  async setUp(): Promise<void> {
    await this.siteI18n.whenReady();
    this.getLang().then(userLang => {
      this.userLang = userLang;
      const pageLang = this.siteI18n.i18n.language;
      const pageVersions = document.querySelectorAll('link[rel="alternate"]');
      let ifHavePageLang = false;
      let pageVersionHref = '';

      pageVersions.forEach((pageVersion: HTMLLinkElement) => {
        if (userLang === pageVersion.hreflang.slice(0, 2) && pageVersion.type !== "application/rss+xml") {
          ifHavePageLang = true;
          pageVersionHref = pageVersion.href;
        }
      });
      if (pageLang !== userLang && ifHavePageLang) {
        const language = {
          en: 'Access english content.',
          pt: 'Acessar conteúdo em português.',
          es: 'Acceda a contenido en español.',
        };
        this.href = pageVersionHref;
        this.chooseLanguage = language[userLang];
        this.open();
      }
    });
  }

  private handleCloseClick() {
    this.setLanguagePreference(this.siteI18n.i18n.language);
    this.close();
  }

  private handleMessageClick() {
    this.setLanguagePreference(this.userLang);
    this.close();
  }

  componentWillLoad() {
    this.siteI18n.whenReady().then(() => {
      this.setUp();
    });

    this.observer = new MutationObserver((mutations: MutationRecord[]) => {
      mutations.forEach(() => {
        this.setUp();
      });
    });

    this.observer.observe(document.head, { childList: true });
  }

  render() {
    return (
      <Host>
        <div
          class={{
            container: true,
            open: this.visible,
          }}
        >
          <a id="page-version-link" href={this.href} title={this.chooseLanguage} onClick={() => this.handleMessageClick()}>
            {this.chooseLanguage}
          </a>
          <sy-icon name="close" onClick={() => this.handleCloseClick()}></sy-icon>
        </div>
      </Host>
    );
  }
}
